import { saveAs } from 'file-saver';
import millify from 'millify';
import {
  ColorOrange600,
  ColorRed600,
  ColorViolet600,
} from 'ui/src/design-system/colors';
import { FiltersForm } from '@shared/types/forms';
import { getNewDate, toISOString } from '@utils/dates';
import { removeDecimals } from '@utils/numbers';
import { isEmptyString } from '@utils/strings';
import {
  Case,
  GetCaseAggregateResultsResponse,
} from 'services/ClaimscoreApiService/Cases/types';
import { DashboardFilters } from 'services/ClaimscoreApiService/shared/types';

export const spikeColorByIndex = [ColorOrange600, ColorViolet600, ColorRed600];

const EndOfDayTime = { hour: 23, minute: 59, second: 59, millisecond: 0 };

export const mapDatesToTimezoneDates = (startDate = '', endDate = '') => {
  if (isEmptyString(startDate) || isEmptyString(endDate))
    return { startDateUTC: '', endDateUTC: '' };

  const startDateUTC = toISOString(new Date(startDate));
  const endDateUTC = getNewDate(endDate);
  endDateUTC.setUTCHours(
    EndOfDayTime.hour,
    EndOfDayTime.minute,
    EndOfDayTime.second,
    EndOfDayTime.millisecond,
  );
  return { startDateUTC, endDateUTC: toISOString(endDateUTC) };
};

export const mapFormToPayload = (
  filters: FiltersForm,
  selectedCase: Case,
): DashboardFilters => {
  const {
    deductionCodes,
    determinations,
    payoutRange,
    scoreRange,
    unitRange,
    startDate,
    endDate,
    claimTypes,
  } = filters;

  const hasDetermination = determinations && determinations.length > 0;
  const hasUnitRange = unitRange && unitRange.length > 0;
  const hasPayoutRange = payoutRange && payoutRange.length > 0;
  const hasScoreRange = scoreRange && scoreRange.length > 0;
  const hasClaimTypes = claimTypes && claimTypes.length > 0;
  const hasDeductionCode = deductionCodes && deductionCodes.length > 0;
  const { startDateUTC, endDateUTC } = mapDatesToTimezoneDates(
    startDate,
    endDate,
  );

  return {
    dateRange: {
      startDate:
        startDateUTC ||
        toISOString(new Date(selectedCase.claimSubmissionStartDate)),
      endDate:
        endDateUTC ||
        toISOString(new Date(selectedCase.claimSubmissionEndDate)),
    },
    determinations: hasDetermination ? determinations : undefined,
    unitRange: hasUnitRange
      ? {
          min: Number(unitRange?.[0]),
          max: Number(unitRange?.[1]),
        }
      : undefined,
    payoutRange: hasPayoutRange
      ? {
          min: Number(payoutRange?.[0] || 0) * 100,
          max: Number(payoutRange?.[1] || 0) * 100,
        }
      : undefined,
    scoreRange: hasScoreRange
      ? {
          min: Number(scoreRange?.[0]),
          max: Number(scoreRange?.[1]),
        }
      : undefined,
    deductionCodes: hasDeductionCode ? deductionCodes : undefined,
    claimTypes: hasClaimTypes ? claimTypes : undefined,
  };
};

interface Spike {
  spikeID: string;
  title: string;
  totalClaims: number;
  VSaverage: number;
  borderColor: string;
  claimData: { key: string; value: string }[];
  blocks: { title: string; info: { value: string; link: string }[] }[];
}

export const keysToParse = [
  'unitRange',
  'payoutRange',
  'scoreRange',
  'claimTypes',
  'deductionCodes',
  'determinations',
];

export const decodeSearchParams = (searchParams: URLSearchParams) =>
  [...searchParams.entries()].reduce((acc, [key, val]) => {
    if (val === 'true' || val === 'false') {
      return {
        ...acc,
        [key]: val === 'true',
      };
    }
    if (!keysToParse.includes(key)) {
      return {
        ...acc,
        [key]: val,
      };
    }
    try {
      return {
        ...acc,
        [key]: val.includes(',') ? val.split(',') : [val],
      };
    } catch {
      return {
        ...acc,
        [key]: undefined,
      };
    }
  }, {});

export const mapAggregateToSpikes = (
  buckets: GetCaseAggregateResultsResponse['buckets'],
): Spike[] => {
  const spikeBuckets = buckets.filter((bucket) => bucket.isSpike);
  const totalBuckets = buckets.filter(
    (bucket) =>
      bucket.deductionCodesCount && bucket.deductionCodesCount.length > 0,
  );

  const averageClaims =
    buckets.reduce(
      (acc, curr) =>
        acc + curr.totalApproved + curr.totalPending + curr.totalRejected,
      0,
    ) / (totalBuckets.length || 1);

  return spikeBuckets.map((bucket, index) => {
    const {
      totalApproved,
      totalPending,
      totalRejected,
      sourceCount,
      deductionCodesCount,
      totalClaims,
    } = bucket;
    const approvedPercentage = removeDecimals(
      (totalApproved / totalClaims) * 100,
    );
    const pendingPercentage = removeDecimals(
      (totalPending / totalClaims) * 100,
    );
    const rejectedPercentage = removeDecimals(
      (totalRejected / totalClaims) * 100,
    );
    return {
      title: `Spike ${index + 1}`,
      spikeID: bucket.bucketID,
      totalClaims,
      VSaverage: removeDecimals((totalClaims / averageClaims) * 100),
      borderColor: spikeColorByIndex[index],
      claimData: [
        {
          key: 'Approved',
          value: approvedPercentage === 0 ? 'N/A' : `${approvedPercentage}%`,
        },
        {
          key: 'Pending',
          value: pendingPercentage === 0 ? 'N/A' : `${pendingPercentage}%`,
        },
        {
          key: 'Rejected',
          value: rejectedPercentage === 0 ? 'N/A' : `${rejectedPercentage}%`,
        },
      ],
      blocks: [
        {
          title: 'Top Sources',
          info: (
            sourceCount?.sort((a, b) => b.count - a.count).slice(0, 3) || []
          ).map(({ source, count }) => ({
            link: source,
            value: millify(count),
          })),
        },
        {
          title: 'Top Rejected',
          info: (
            deductionCodesCount
              ?.sort((a, b) => b.count - a.count)
              .slice(0, 3) || []
          ).map(({ deductionCode, count }) => ({
            link: deductionCode,
            value: millify(count),
          })),
        },
      ],
    };
  });
};

const fileTypeMap = {
  pdf: 'application/pdf',
  csv: 'text/csv',
  zip: 'application/zip',
};

export const generateReportCaseFile = ({
  caseName,
  todayDate,
  file,
  fileType,
}: {
  caseName: string;
  todayDate: string;
  file: string;
  fileType: 'pdf' | 'csv' | 'zip';
}) => {
  const blob = new Blob([file], {
    type: fileTypeMap[fileType],
  });
  saveAs(blob, `ClaimScore Report - ${caseName} ${todayDate}.${fileType}`);
};

export const getInitialDate = (date: string, option: string) => date || option;
